import { graphql } from 'gatsby';
import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import Call from '../components/Call';
import Layout from '../components/Layout';
import SEO from '../components/SEO';

const maxLength = 500;

// Turnstile site keys are public and safe to embed in client code.
// Overridable via GATSBY_TURNSTILE_SITE_KEY at build time.
const TURNSTILE_SITE_KEY =
  process.env.GATSBY_TURNSTILE_SITE_KEY || '0x4AAAAAAD19g3Rph38nsjcx';

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';
const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// Load the Turnstile script once and resolve when window.turnstile is ready.
// Explicit rendering (not the implicit auto-render) is required in a Gatsby SPA:
// client-side navigation swaps the widget's DOM node, so we must render and
// remove the widget ourselves to avoid "already loaded" / "cannot find widget".
const loadTurnstile = () =>
  new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }
    const existing = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.turnstile));
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = reject;
    document.head.appendChild(script);
  });

const Contact = ({ data }) => {
  const { title } = data.markdownRemark.frontmatter;
  const { html } = data.markdownRemark;

  const [captchaToken, setCaptchaToken] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    let widgetId;
    let cancelled = false;

    loadTurnstile()
      .then(turnstile => {
        if (cancelled || !turnstile || !containerRef.current) return;
        widgetId = turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: token => setCaptchaToken(token),
          'expired-callback': () => setCaptchaToken(''),
          'error-callback': () => setCaptchaToken('')
        });
      })
      .catch(error => {
        console.error('Failed to load Turnstile:', error);
      });

    return () => {
      cancelled = true;
      if (widgetId !== undefined && window.turnstile) {
        window.turnstile.remove(widgetId);
      }
    };
  }, []);

  return (
    <Layout bodyClass="page-default-single">
      <SEO title={title} />
      <div className="container pb-6 pt-6 pt-md-10 pb-md-10">
        <div className="row justify-content-start">
          <div className="col-12 col-md-7 col-lg-6 order-2 order-md-1">
            <h1 className="title">{title}</h1>
            <Call showButton={false} />
          </div>
          <div className="col-12 col-md-5 col-lg-6 order-1 order-md-2 position-relative">
            <MapContainer 
              className='intro-image-map'
              center={[46.1519696,19.5787726]} zoom={15} scrollWheelZoom={false} attributionControl={false} >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={[46.1519696,19.5787726]}>
                <Popup>
                  Kladomox Plus
                </Popup>
              </Marker>
            </MapContainer>
          </div>
        </div>
      </div>

      <div className="strip">
        <div className="container pt-6 pb-6 pb-md-10">
          <div className="row justify-content-start">
            <div className="col-12 col-md-7 col-lg-6 order-2 order-md-1">
              <div className="content mt-4" dangerouslySetInnerHTML={{ __html: html }} />
            </div>
          </div>
        </div>
      </div>
      <div className="strip">
        <form
          className='container col- pt-6 pb-6 pb-md-10'
          method="post"
          target="dummyframe"
          action="https://rest.kladomoxplus.com/contact">
          <label htmlFor="name" className='d-flex mb-1'>Ime</label>
          <input className='d-flex mb-1' type="text" name="name" />
          
          <label htmlFor='email' className='d-flex mb-1'>
          E-mail
          </label>
          <input className='d-flex mb-1' type="email" name="email" />
          <label htmlFor='phone' className='d-flex mb-1'>
          Broj telefona
          </label>
          <input type="text" name="phone" />
          <label htmlFor='message' className='d-flex mb-1'>
          Poruka
          </label>
          <textarea
            className='d-flex flex-column  mb-1 message-area'
            maxLength={maxLength}
            rows={10}
            name='message'
          />
          <div ref={containerRef} className="mt-2 mb-3" />
          <input type="submit" value="POŠALJITE UPIT" disabled={!captchaToken} />
        </form>
      </div>
    </Layout>
  );
};

export const query = graphql`
  query($id: String!) {
    markdownRemark(id: { eq: $id }) {
      frontmatter {
        title
        path
      }
      fields {
        slug
      }
      html
    }
}
`;

export default Contact;
